import {
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import PaginatedTable from '../../Common/PaginatedTable';
import { HiEye, HiPencilAlt, HiXCircle } from 'react-icons/hi';
import { displayDateOnly, formatNumbers } from '../../../Utils';
import { Delivery } from '../../../Models/Delivery';
import { SiQuickbooks } from 'react-icons/si';
import { DeliveryStatusEnum } from '../../../Models/DeliveryStatus';
import { FaFileInvoiceDollar } from 'react-icons/fa';

export default function DeliveryListTable({
  data,
  deleteDelivery,
  editDelivery,
  sendToQuickbooks,
  viewDropTicket,
  viewInvoice,
}: {
  data: Delivery[];
  deleteDelivery(id: number): void;
  editDelivery: (inv: Delivery) => void;
  sendToQuickbooks: (d: Delivery) => Promise<void>;
  viewDropTicket: (d: Delivery) => Promise<void>;
  viewInvoice: (d: Delivery) => Promise<void>;
}) {
  const backgroundColor = (statusId: number, quickBooksId: string | null) => {
    if (quickBooksId !== null) {
      return 'buttonMainAlt';
    }
    if (statusId === DeliveryStatusEnum.Approved) {
      return 'buttonMain';
    }
    if (statusId === DeliveryStatusEnum.Invoiced && quickBooksId === null) {
      return 'buttonMainAlt';
    }

    return 'buttonMainDisabled';
  };
  const columnHelper = createColumnHelper<Delivery>();

  const columns = [
    columnHelper.accessor((row) => row.id, {
      id: 'id',
      header: '',
      cell: (info) => (
        <div className='flex justify-center gap-1'>
          <button
            type='button'
            className='buttonMain p-2'
            onClick={() => editDelivery(info.row.original)}
          >
            <HiPencilAlt />
          </button>
          <button
            type='button'
            className='buttonMain p-2'
            onClick={() => viewDropTicket(info.row.original)}
          >
            <HiEye />
          </button>
          <button
            type='button'
            className='buttonMain p-2'
            onClick={() => viewInvoice(info.row.original)}
          >
            <FaFileInvoiceDollar />
          </button>
        </div>
      ),
      footer: (info) => info.column.id,
      size: 35,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.client?.id, {
      id: 'clientId',
      header: 'Client',
      cell: (info) => (
        <i
        // className='cursor-pointer text-blue-600'
        // onClick={() => editDelivery(info.row.original)}
        >
          {info.row.original.client?.name}
        </i>
      ),
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
      size: 35,
    }),
    columnHelper.accessor((row) => row.clientLocation?.id, {
      id: 'clientLocationId',
      header: 'Location',
      cell: (info) => (
        <i
        // className='cursor-pointer text-blue-600'
        // onClick={() => editDelivery(info.row.original)}
        >
          {info.row.original.clientLocation?.address}{' '}
          {info.row.original.clientLocation?.city}{' '}
          {info.row.original.clientLocation?.state
            ? info.row.original.clientLocation?.state.name
            : ''}
        </i>
      ),
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.startTime, {
      id: 'startTime',
      header: 'Start',
      cell: (info) => (
        <i>
          {displayDateOnly(info.getValue() ? new Date(info.getValue()!) : null)}
        </i>
      ),
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
      size: 35,
    }),
    columnHelper.accessor((row) => row.endTime, {
      id: 'endTime',
      header: 'End',
      cell: (info) => (
        <i>
          {displayDateOnly(info.getValue() ? new Date(info.getValue()!) : null)}
        </i>
      ),
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
      size: 35,
    }),
    columnHelper.accessor((row) => null, {
      id: 'deliveryItems',
      header: 'Delivery Items',
      cell: (info) => <i>{info.row.original.deliveryItems?.length}</i>,
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
      size: 35,
    }),
    columnHelper.accessor((row) => row.status?.name, {
      id: 'statusId',
      header: 'Status',
      // cell: (info) => <i>{info.row.original.deliveryItems?.length}</i>,
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
      size: 35,
    }),
    columnHelper.accessor((row) => row.id, {
      id: 'quickbooksid',
      header: 'Send to Quickbooks',
      cell: (info) => (
        <div className='flex justify-center gap-1'>
          <button
            type='button'
            className={`${backgroundColor(
              info.row.original.statusId ?? 0,
              info.row.original.quickBooksId
            )} p-2`}
            onClick={() => sendToQuickbooks(info.row.original)}
            disabled={
              info.row.original.statusId === DeliveryStatusEnum.InProcess ||
              info.row.original.statusId === DeliveryStatusEnum.Submitted ||
              info.row.original.statusId === DeliveryStatusEnum.Canceled
            }
            hidden={info.row.original.statusId === DeliveryStatusEnum.Canceled}
          >
            <SiQuickbooks />
          </button>
        </div>
      ),
      footer: (info) => info.column.id,
      size: 35,
      enableColumnFilter: false,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      sorting: [
        {
          id: 'id',
          desc: false,
        },
      ],
    },
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <>
      <div className='w-full mx-auto overflow-x-auto'>
        <PaginatedTable
          table={table}
          dataCount={data.length}
          columnResizeMode='onChange'
          showFilters={true}
        />
      </div>
    </>
  );
}
